import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { GainLossRowData, GainLossTableRenderer } from '../gain-loss-table-renderer';
import { UnrealizedAllocationMobxDto } from '../../../../../../models/mobx/dtos/statistic/unrealized-allocation-mobx-dto';
import { AllocationsGainLossEnum } from '../../../../../../models/enums/allocations-gain-loss-enum';

export const TotalTableContent: React.FC<{ type: AllocationsGainLossEnum }> = observer(({ type }) => {
    const { symbolPriceUpdateStore, statisticStore, portfolioStore } = useRootStore();
    const { ratiosStore } = statisticStore;

    const gainLossData = useMemo(() => {
        const { allUnrealizedAllocations, allRealizedAllocations } = ratiosStore;
        const isGain = type === AllocationsGainLossEnum.Gain;

        const combinedAllocations = [...allUnrealizedAllocations, ...allRealizedAllocations];

        const aggregatedTotalsMap = new Map<string, { invested: number; amount: number }>();
        combinedAllocations.forEach((alloc) => {
            const current = aggregatedTotalsMap.get(alloc.symbolId) || { invested: 0, amount: 0 };
            aggregatedTotalsMap.set(alloc.symbolId, {
                invested: current.invested + (alloc.invested || 0),
                amount: current.amount + (alloc.amount || 0),
            });
        });
        const processedSymbolIds = new Set<string>();

        const ranking = combinedAllocations.reduce<GainLossRowData[]>((acc, curr) => {
            if (processedSymbolIds.has(curr.symbolId) || curr.amount <= 0) {
                return acc;
            }

            let profitValue = curr.totalProfitValue + curr.dividendsValue - curr.feesValue;

            if (curr instanceof UnrealizedAllocationMobxDto) {
                const todayProfit = symbolPriceUpdateStore.getTodayProfitBySymbolId(
                    curr.referenceSymbolId ?? curr.symbolId,
                    curr.value,
                    curr.amount,
                    curr.dateTo,
                );
                profitValue += todayProfit;
            }

            const isMatch = isGain ? profitValue > 0 : profitValue < 0;
            if (!isMatch) {
                processedSymbolIds.add(curr.symbolId);
                return acc;
            }

            const aggregatedTotals = aggregatedTotalsMap.get(curr.symbolId) || { invested: 0, amount: 0 };
            const profitPercentage =
                aggregatedTotals.invested > 0 ? (profitValue / aggregatedTotals.invested) * 100 : 0;

            acc.push({
                ...curr,
                profitValue,
                profitPercentage,
                amount: aggregatedTotals.amount,
            });

            processedSymbolIds.add(curr.symbolId);
            return acc;
        }, []);

        return ranking
            .sort((a, b) => (isGain ? b.profitValue - a.profitValue : a.profitValue - b.profitValue))
            .slice(0, 5);
    }, [ratiosStore.allUnrealizedAllocations, ratiosStore.allRealizedAllocations, symbolPriceUpdateStore, type]);

    return (
        <GainLossTableRenderer
            loading={ratiosStore.loading}
            isRecalculating={portfolioStore.selectedPortfolio.isRecalculating}
            gainLossData={gainLossData}
        />
    );
});
